<template>
  <v-card
    class="mx-auto"
    :class="props.cardClass"
    elevation="8"
    :max-width="props.cardMaxWidth"
    rounded="lg"
  >
    <div class="d-flex py-3 justify-space-between">
      <v-list-item>
        <ParentSelectors
          v-if="isNewObject"
          :object-type="props.contentType"
          type="object-creation"
        />
        <slot name="content"></slot>

        <v-card
          v-if="!formValidation.isValid"
          class="mb-12"
          color="red-darken-1"
        >
          <v-card-text
            v-for="(errorMessage, index) in formValidation.errorMessages"
            :key="index"
            class="text-medium-emphasis text-caption"
          >
            <div class="text-white text-left">{{ errorMessage }}</div>
          </v-card-text>
        </v-card>

        <v-card v-if="hasError" class="mb-12" color="red-darken-1">
          <v-card-text class="text-medium-emphasis text-caption">
            <div class="text-white text-left">{{ errorMessage }}</div>
          </v-card-text>
        </v-card>

        <v-card
          v-if="props.warningMessages.length > 0"
          class="mb-12"
          color="yellow-darken-1"
        >
          <v-card-text
            v-for="(warningMessage, index) in props.warningMessages"
            :key="index"
            class="text-medium-emphasis text-caption"
          >
            <div class="text-white text-left">{{ warningMessage }}</div>
          </v-card-text>
        </v-card>

        <v-divider></v-divider>
        <v-list-item>
          <v-btn-group class="my-4">
            <v-btn
              v-if="isEditable"
              color="primary"
              :disabled="!props.canSave"
              @click="save()"
              >{{ $t('components.common.save') }}</v-btn
            >
            <v-btn
              color="error"
              variant="outlined"
              @click="navigateWhenCompleted"
              >{{ $t('components.common.cancel') }}</v-btn
            >
          </v-btn-group>
        </v-list-item>
      </v-list-item>
    </div>
  </v-card>
  <LoadingOverlay v-model:loading="loading" />
</template>

<script setup lang="ts">
import isEqual from 'lodash.isequal'
import { computed, Ref, ref, watch, withDefaults } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { apiClient } from '@/api/ApiClient'
import errorHandler from '@/api/ErrorHandler'
import { clientAppRouteName } from '@/apps/clientAppRegistry'
import {
  getClientAppOfRoute,
  getLocationPreservingState,
  routeHasClientApp,
} from '@/apps/routingUtils'

import { prepareUpdateObject } from './forms/formUtil'
import LoadingOverlay from './LoadingOverlay.vue'
import ParentSelectors from './ParentSelectors.vue'

type BaseFormProps = {
  title?: string
  contentType?: string
  dataObject?: object
  columnWidth: string
  customSubmit?: boolean
  customCreate?: Function
  onCreateFinished?: Function
  onUpdateFinished?: Function
  onCustomSubmit?: Function
  customUpdate?: Function
  preUpdateHook?: Function
  hasError?: boolean
  errorMessage?: string
  canSave?: boolean
  warningMessages?: string[]
  completedLocation?: {
    name: string
    params?: Record<string, string>
    replace?: boolean
  } | null
  cardMaxWidth?: string
  cardClass?: string
}

const router = useRouter()
const route = useRoute()

const props = withDefaults(defineProps<BaseFormProps>(), {
  title: '',
  contentType: '',
  dataObject: () => ({}),
  columnWidth: 'is-12',
  customSubmit: false,
  customCreate: null,
  onCreateFinished: null,
  onUpdateFinished: null,
  onCustomSubmit: null,
  customUpdate: null,
  preUpdateHook: null,
  hasError: false,
  errorMessage: '',
  canSave: true,
  warningMessages: () => [],
  completedLocation: null,
  cardMaxWidth: '448',
  cardclass: 'pa-12 pb-8',
})

let contentType: Ref<string> = ref(null)
let dataObject: Ref<any> = ref(null)
let originalDataObject: Ref<object> = ref({})
let formValidation = ref({
  isValid: true,
  errorMessages: [],
})
let countOriginalChange: Ref<number> = ref(0)
const loading = ref(false)

watch(props.dataObject, (newValue, oldValue) => {
  // Only updates originaldataobject if properties were missing previously,
  if (JSON.stringify(props.dataObject)) {
    countOriginalChange.value++
    if (countOriginalChange.value > 1) {
      if (isSame(originalDataObject, props.dataObject)) {
        originalDataObject.value = JSON.parse(JSON.stringify(props.dataObject))
      }
    } else {
      originalDataObject.value = JSON.parse(JSON.stringify(props.dataObject))
    }
  }
})

function navigateWhenCompleted() {
  if (props.completedLocation !== null) {
    // a location was given via props
    router.push(props.completedLocation)
    return
  } else {
    // try to find a decent route
    if (routeHasClientApp(route)) {
      const clientApp = getClientAppOfRoute(route)
      const routeName = clientAppRouteName(
        clientApp.view_id,
        props.contentType + '-list'
      )
      const location = getLocationPreservingState(routeName, router)
      if (router.resolve(location).matched.length > 0) {
        // only go there if the location matches something
        router.push(location)
        return
      }
    }
  }
  // go somewhere at least
  router.back()
}

function save() {
  if (!props.canSave) {
    return
  }
  if (props.customSubmit) {
    props.onCustomSubmit()
    return
  }
  if (props.preUpdateHook !== null) {
    props.preUpdateHook()
  }
  return Promise.resolve(dataObject.value).then((dataObject) => {
    let promise: Promise<any>
    let isUpdate = true
    if (!dataObject.id || dataObject.id === '0' || dataObject.id === '') {
      isUpdate = false
      if (props.customCreate !== null) {
        promise = props.customCreate(dataObject)
        if (!promise) {
          // for cases where customCreate does not return promise
          return
        }
      } else {
        promise = apiClient.create(props.contentType, dataObject)
      }
    } else {
      if (props.customUpdate !== null) {
        props.customUpdate(dataObject)
        return
      }
      // Only send updated properties
      const updateDiff = prepareUpdateObject(dataObject, originalDataObject)
      promise = apiClient.update(props.contentType, updateDiff)
    }
    loading.value = true
    return promise
      .then((response) => {
        loading.value = false
        if (!isUpdate) {
          if (props.onCreateFinished !== null) {
            props.onCreateFinished(response)
          }
        } else {
          if (props.onUpdateFinished !== null) {
            props.onUpdateFinished(props.dataObject)
          }
        }
        originalDataObject.value = JSON.parse(JSON.stringify(props.dataObject))
        navigateWhenCompleted()
      })
      .catch((error) => {
        loading.value = false
        handleError(error)
      })
  })
}

function clearError() {
  formValidation.value.errorMessages = []
  formValidation.value.isValid = true
}

function handleError(error) {
  formValidation.value.errorMessages = errorHandler.errorToStrings(error)
  formValidation.value.isValid = false
}

function isSame(obj1, obj2) {
  for (const v in obj1) {
    if (
      (obj1[v] === '0' && obj2[v] !== '0') ||
      (obj1[v] !== '0' && obj2[v] === '0')
    ) {
      continue
    } else if (!isEqual(obj1[v], obj2[v])) {
      return false
    }
  }
  return true
}

const isEditable = computed(() => {
  if (
    dataObject.value?._permissions &&
    dataObject.value?._permissions.hasOwnProperty('edit')
  ) {
    return dataObject.value._permissions.edit
  } else {
    return true
  }
})

const isNewObject = computed(() => {
  return dataObject.value?.id === undefined || dataObject.value?.id === '0'
})

defineExpose({
  isNewObject,
  handleError,
})
</script>

<style lang="scss" scoped>
.validation-message {
  margin-top: 10px;
}
</style>
